import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { isPlatformHost } from '@/lib/platformHost';
import { getLegacyCredentialRedirect } from '@/lib/legacyRedirect';
import { AlertCircle } from 'lucide-react';
import { PublicBlockRenderer } from './PublicBlockRenderer';
import { PublicRelatedPosts } from './PublicRelatedPosts';
import { PublicHeader } from './PublicHeader';
import { PublicFooter } from './PublicFooter';
import { AdminBar } from './AdminBar';
import { CookieBanner } from '@/features/legal/components/CookieBanner';
import { CustomScriptsInjector } from './CustomScriptsInjector';
import { PublicSiteMeta } from './PublicSiteMeta';
import { GoogleAnalyticsInjector } from '@/features/analytics/components/GoogleAnalyticsInjector';
import { PublicViewTracker } from '@/features/analytics/components/PublicViewTracker';
import { getTemplateIdForContent, resolveAppearance, appearanceCssVars, type PublicBranding } from '../lib/brandingVars';
import { loadGoogleFont } from '@/features/themes/lib/appearance';
import { usePublicMenu } from '@/features/themes/lib/useSiteMenu';
import { useSiteChrome } from '../lib/useSiteChrome';
import { getPureHtmlContent } from '../lib/pureHtmlPage';
import type { Block } from '@/types/blocks';

interface SiteSeoSettings {
  meta_title?: string;
  meta_description?: string;
  og_image?: string;
}

function getContentSeo(value: unknown): SiteSeoSettings {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const record = value as Record<string, unknown>;
  return {
    meta_title: typeof record.meta_title === 'string' ? record.meta_title : '',
    meta_description: typeof record.meta_description === 'string' ? record.meta_description : '',
    og_image:
      typeof record.og_image === 'string'
        ? record.og_image
        : typeof record.image === 'string'
          ? record.image
          : '',
  };
}

function getSiteSeo(themeSettings: unknown): SiteSeoSettings {
  if (!themeSettings || typeof themeSettings !== 'object' || Array.isArray(themeSettings)) return {};
  const seo = (themeSettings as { seo?: unknown }).seo;
  if (!seo || typeof seo !== 'object' || Array.isArray(seo)) return {};
  return seo;
}

function formatPostDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function resolveRedirectUrl(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed || typeof window === 'undefined') return null;
  try {
    const url = trimmed.startsWith('/') && !trimmed.startsWith('//')
      ? new URL(trimmed, window.location.origin)
      : new URL(trimmed);
    if (!['http:', 'https:'].includes(url.protocol)) return null;
    return url.href === window.location.href ? null : url.href;
  } catch {
    return null;
  }
}

/**
 * Renderiza uma página publicada do site pelo slug, no mesmo shell público
 * (header com menu + rodapé), aplicando a aparência resolvida (site + modelo
 * vinculado à página). Destino dos links internos do menu.
 */
export function PublicPageView() {
  const { slug: routeSlug, pageSlug } = useParams<{ slug: string; pageSlug: string }>();
  const legacyRedirectUrl = getLegacyCredentialRedirect();

  // Sem :slug na rota (URL limpa em domínio custom, /:pageSlug): resolve o
  // site pelo hostname, mesmo padrão de PublicSitePage/RootDispatch. Em host
  // da plataforma isso fica desligado — um path de 1 segmento sem :slug ali
  // é conteúdo mesmo inexistente, não deve tentar resolver por hostname.
  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const platformHost = isPlatformHost(hostname);

  const { data: resolvedSite, isLoading: resolvingHost } = useQuery({
    queryKey: ['public-site-host', hostname],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('resolve_site_by_hostname', { input_hostname: hostname });
      if (error) throw error;
      return data?.[0] ?? null;
    },
    enabled: !routeSlug && !!hostname && !platformHost,
  });

  const slug = routeSlug ?? resolvedSite?.slug;

  const { data: site, isLoading: loadingSiteRow } = useQuery({
    queryKey: ['public-site', slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from('sites')
        .select('id, name, slug, front_page_mode, home_page_id, theme_settings, site_branding(*)')
        .eq('slug', slug)
        .eq('active', true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  const loadingSite = loadingSiteRow || (!routeSlug && resolvingHost);

  const { data: page, isLoading: loadingPage } = useQuery({
    queryKey: ['public-page', site?.id, pageSlug],
    queryFn: async () => {
      if (!site?.id || !pageSlug) return null;
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from('posts')
        .select('id, title, excerpt, content, type_id, template_id, layout, published_at, seo, og, redirect_enabled, redirect_url, redirect_status')
        .eq('site_id', site.id)
        .eq('slug', pageSlug)
        .or(`status.eq.published,and(status.eq.scheduled,scheduled_at.lte.${nowIso})`)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!site?.id && !!pageSlug,
    refetchInterval: 60_000,
  });

  const branding = site?.site_branding as PublicBranding | null;
  const appearanceTemplateId = getTemplateIdForContent(branding, page?.template_id, page?.type_id ? 'post' : 'page');

  const { data: template } = useQuery({
    queryKey: ['appearance-template', appearanceTemplateId],
    queryFn: async () => {
      if (!appearanceTemplateId) return null;
      const { data, error } = await supabase.from('appearance_templates').select('*').eq('id', appearanceTemplateId).single();
      if (error) throw error;
      return data;
    },
    enabled: !!appearanceTemplateId,
  });

  const isBlank = page?.layout === 'blank';

  const { data: menu } = usePublicMenu(isBlank ? undefined : site?.id);

  const siteChromeBranding = site?.site_branding as PublicBranding & { header_template_id?: string | null; footer_template_id?: string | null };
  const { headerTemplate, footerTemplate } = useSiteChrome(
    isBlank ? null : siteChromeBranding?.header_template_id,
    isBlank ? null : siteChromeBranding?.footer_template_id,
    siteChromeBranding,
  );
  const siteName = branding?.app_name ?? site?.name ?? 'Lumenise';
  const siteUrl = typeof window !== 'undefined'
    ? (platformHost ? `${window.location.origin}/s/${site?.slug ?? slug}/home` : `${window.location.origin}/`)
    : undefined;
  const seo = getSiteSeo(site?.theme_settings);
  const siteBrowserTitle = seo.meta_title?.trim() || branding?.browser_title?.trim() || siteName;
  const pageSeo = getContentSeo(page?.seo);
  const pageOg = getContentSeo(page?.og);
  const isHomePage = page?.id === site?.home_page_id;
  // Título SEO customizado já é final. Conteúdo sem sobrescrita recebe apenas
  // um sufixo de marca, igual à regra usada pelo worker de borda.
  const browserTitle = pageSeo.meta_title?.trim()
    || (!isHomePage && page?.title ? `${page.title} | ${siteName}` : siteBrowserTitle);
  const seoDescription = pageSeo.meta_description?.trim() || page?.excerpt?.trim() || seo.meta_description?.trim();
  const socialImageUrl = pageOg.og_image?.trim() || pageSeo.og_image?.trim() || seo.og_image?.trim() || branding?.social_image_url;
  const appearance = resolveAppearance(branding, template);
  const isBoxed = appearance.layout_mode === 'boxed';
  const formattedPostDate = formatPostDate(page?.published_at);
  const redirectUrl = page?.redirect_enabled ? resolveRedirectUrl(page.redirect_url) : null;

  useEffect(() => {
    loadGoogleFont(appearance.heading_font);
    loadGoogleFont(appearance.body_font);
  }, [appearance.heading_font, appearance.body_font]);

  useEffect(() => {
    if (!redirectUrl) return;
    window.location.replace(redirectUrl);
  }, [redirectUrl]);

  useEffect(() => {
    if (!legacyRedirectUrl) return;
    window.location.replace(legacyRedirectUrl);
  }, [legacyRedirectUrl]);

  if (legacyRedirectUrl) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
      </div>
    );
  }

  if (loadingSite || loadingPage) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
      </div>
    );
  }

  if (!site || !page) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white px-6 text-slate-900">
        <div className="max-w-md space-y-3 text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-rose-500" />
          <h1 className="font-display text-2xl font-bold">Página não encontrada</h1>
          <p className="text-sm text-slate-500">Esta página não existe ou ainda não foi publicada.</p>
        </div>
      </div>
    );
  }

  if (redirectUrl) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
      </div>
    );
  }

  const blocks = (page.content as unknown as Block[]) || [];
  const templateBlocks = template?.template_mode === 'dynamic'
    ? ((template.content as unknown as Block[]) || [])
    : null;
  const renderBlocks = templateBlocks ?? blocks;
  const pureHtml = getPureHtmlContent(page.layout, renderBlocks);

  // Página "Em branco" com um único bloco HTML: sem cabeçalho/rodapé. O
  // aviso de cookies continua fora do iframe para que visitantes possam
  // consentir com o Analytics também nesses conteúdos.
  if (pureHtml !== null) {
    return (
      <div className="flex min-h-screen flex-col">
        <CustomScriptsInjector
          headCode={branding?.custom_head_code}
          bodyStartCode={branding?.custom_body_start_code}
          bodyEndCode={branding?.custom_body_end_code}
        />
        <GoogleAnalyticsInjector measurementId={branding?.google_analytics_measurement_id} />
        <PublicViewTracker postId={page.id} />
        <PublicSiteMeta
          title={browserTitle}
          description={seoDescription}
          faviconUrl={branding?.favicon_url}
          socialImageUrl={socialImageUrl}
          siteName={siteName}
          siteUrl={siteUrl}
          alternateName={site?.name}
          isHomePage={isHomePage}
          type={page.type_id ? 'article' : 'website'}
          publishedTime={page.type_id ? page.published_at : undefined}
        />
        <AdminBar siteId={site.id} postId={page.id} />
        <iframe
          srcDoc={pureHtml}
          title={page.title || 'Conteúdo'}
          allow="autoplay; fullscreen; picture-in-picture"
          className="block w-full flex-1 border-0"
        />
        <CookieBanner siteKey={site?.id} />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-white text-slate-900"
      style={{
        ...appearanceCssVars(appearance),
        backgroundColor: 'var(--color-page-bg)',
        backgroundImage: 'var(--bg-image-page)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <CustomScriptsInjector
        headCode={branding?.custom_head_code}
        bodyStartCode={branding?.custom_body_start_code}
        bodyEndCode={branding?.custom_body_end_code}
      />
      <GoogleAnalyticsInjector measurementId={branding?.google_analytics_measurement_id} />
      <PublicViewTracker postId={page.id} />
      <PublicSiteMeta
        title={browserTitle}
        description={seoDescription}
        faviconUrl={branding?.favicon_url}
        socialImageUrl={socialImageUrl}
        siteName={siteName}
        siteUrl={siteUrl}
        alternateName={site?.name}
        isHomePage={isHomePage}
        type={page.type_id ? 'article' : 'website'}
        publishedTime={page.type_id ? page.published_at : undefined}
      />
      <AdminBar siteId={site.id} postId={page.id} />

      {!isBlank && (
        <PublicHeader
          siteSlug={site.slug}
          siteName={siteName}
          logoUrl={branding?.logo_url}
          menu={menu ?? []}
          siteId={site.id}
          customBlocks={headerTemplate?.blocks}
          customAppearance={headerTemplate?.appearance}
        />
      )}

      <main
        className={isBoxed ? 'mx-auto w-full overflow-visible shadow-xl' : undefined}
        style={{
          padding: 'var(--page-padding)',
          marginTop: 'var(--page-margin)',
          marginBottom: 'var(--page-margin)',
          ...(isBoxed
            ? {
                maxWidth: 'min(var(--box-width), 100%)',
                borderRadius: 'var(--box-radius)',
                backgroundColor: 'var(--color-box-bg)',
                backgroundImage: 'var(--bg-image-box)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : undefined),
        }}
      >
        {/*
          Só para posts (type_id preenchido): o importador de WordPress e o
          editor nunca inserem um bloco de título no conteúdo — o título
          sempre foi para ser exibido separado. Páginas não ganham isso de
          graça porque normalmente montam seu próprio título via blocos
          (hero, heading customizado etc.); forçar um aqui duplicaria.
        */}
        {!isBlank && page.type_id && (
          <div className="mx-auto max-w-3xl px-6 pb-6 pt-12 text-center">
            <h1
              className="text-4xl font-extrabold tracking-tight md:text-5xl"
              style={{ color: 'var(--color-heading, #0f172a)', fontFamily: 'var(--font-heading, var(--font-display))' }}
            >
              {page.title}
            </h1>
            {formattedPostDate && (
              <p
                className="mt-3 text-xs font-medium uppercase tracking-[0.18em]"
                style={{ color: 'color-mix(in oklab, var(--color-paragraph, #475569), transparent 28%)' }}
              >
                {formattedPostDate}
              </p>
            )}
          </div>
        )}
        <PublicBlockRenderer blocks={renderBlocks} siteId={site.id} siteSlug={site.slug} fallbackImageAlt={page.title} />
        {page.type_id && <PublicRelatedPosts siteId={site.id} postId={page.id} siteSlug={site.slug} platformHost={platformHost} />}
      </main>

      {!isBlank && (
        <PublicFooter
          siteName={siteName}
          siteId={site.id}
          siteSlug={site.slug}
          customBlocks={footerTemplate?.blocks}
          customAppearance={footerTemplate?.appearance}
        />
      )}
      <CookieBanner siteKey={site?.id} />
    </div>
  );
}
